/**
 * Divisions Management Page - Create and manage organizational divisions
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import adminApi from '../api/adminApi'
import type { Division, CreateDivisionRequest, UpdateDivisionRequest } from '../types/admin.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const DivisionsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingDivision, setEditingDivision] = useState<Division | null>(null)
  const [formData, setFormData] = useState<CreateDivisionRequest>({
    name: '',
    code: '',
    description: '',
  })

  // Check if user is admin
  const isAdmin = user?.roles?.some(role => role.name === 'admin')

  // Redirect non-admin users to main page
  useEffect(() => {
    if (user && !isAdmin) {
      toast.error('You do not have permission to access this page')
      navigate('/')
    }
  }, [user, isAdmin, navigate])

  // Fetch divisions
  const { data, isLoading } = useQuery({
    queryKey: ['adminDivisions', { include_inactive: true }],
    queryFn: () => adminApi.getDivisions({ include_inactive: true }),
  })

  const divisions = data?.divisions || []

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateDivisionRequest) => adminApi.createDivision(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDivisions'] })
      toast.success('Division created successfully')
      setShowCreateModal(false)
      resetForm()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to create division')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDivisionRequest }) =>
      adminApi.updateDivision(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDivisions'] })
      toast.success('Division updated successfully')
      setEditingDivision(null)
      resetForm()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update division')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteDivision(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDivisions'] })
      toast.success('Division deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete division')
    },
  })

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
    })
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.code) {
      toast.error('Name and code are required')
      return
    }
    createMutation.mutate(formData)
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingDivision) return
    if (!formData.name || !formData.code) {
      toast.error('Name and code are required')
      return
    }
    updateMutation.mutate({
      id: editingDivision.id,
      data: {
        name: formData.name,
        code: formData.code,
        description: formData.description || undefined,
      },
    })
  }

  const handleDelete = (division: Division) => {
    if (confirm(`Are you sure you want to delete "${division.name}"?`)) {
      deleteMutation.mutate(division.id)
    }
  }

  const handleToggleActive = (division: Division) => {
    updateMutation.mutate({
      id: division.id,
      data: { is_active: !division.is_active },
    })
  }

  const startEdit = (division: Division) => {
    setEditingDivision(division)
    setFormData({
      name: division.name,
      code: division.code,
      description: division.description || '',
    })
  }

  // Show loading while checking auth or redirecting
  if (!user || !isAdmin || isLoading) return <LoadingPage />

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/admin" className="hover:text-red-600">Admin</Link>
            <span>/</span>
            <span>Divisions</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Division Management</h1>
          <p className="text-gray-600 mt-1">Create and manage organizational divisions</p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Division
        </button>
      </div>

      {/* Divisions Grid */}
      {divisions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Divisions Yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first division</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Create Division
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {divisions.map((division) => (
            <div
              key={division.id}
              className={`bg-white rounded-lg shadow border-l-4 ${
                division.is_active ? 'border-green-500' : 'border-gray-300'
              }`}
            >
              <div className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded mb-2">
                      {division.code}
                    </span>
                    <h3 className="font-semibold text-gray-900">{division.name}</h3>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      division.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {division.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {division.description && (
                  <p className="text-sm text-gray-500 mb-3">{division.description}</p>
                )}

                {division.head && (
                  <div className="text-sm text-gray-600 mb-3">
                    <span className="text-gray-400">Head:</span> {division.head.full_name}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => startEdit(division)}
                    className="flex-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(division)}
                    className={`flex-1 px-3 py-1.5 text-sm rounded ${
                      division.is_active
                        ? 'text-orange-600 hover:bg-orange-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {division.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(division)}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingDivision) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingDivision ? 'Edit Division' : 'Create Division'}
            </h3>
            <form onSubmit={editingDivision ? handleUpdate : handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Division Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Engineering"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Division Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., ENG"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent uppercase"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Short unique identifier (will be uppercase)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this division..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditingDivision(null)
                    resetForm()
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingDivision
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default DivisionsPage
